import { withListOverflow } from "../components/ListOverflow.jsx";
export const PlaybookRunsPage = withListOverflow(PlaybookRunsListOwner);
// src/v2/playbooks/PlaybookRunsPage.jsx
//
// G-34-S2: Playbook runs list.
//
// Route: /playbooks/runs
//
// Features:
//   - Status tabs: All / Active / Completed / Cancelled (with counts)
//   - Search input (name / target / owner)
//   - Filter popover placeholder (type, owner, date range)
//   - Sort dropdown (last activity / started / completed / progress)
//   - Table with: Playbook, Target, Status, Progress (mini-bar + label), Owner, Started, Last activity / Outcome, Open/View link
//   - Completed + cancelled rows show "View →" and link to read-only runner

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import { T } from "../../app/tokens.js";
import { getPlaybookRuns } from "../../data/playbooksClient.js";
import { log } from "../../utils/logger.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ICON_MAP = {
  "closure":            "⇄",
  "relocation":         "⇄",
  "office closure":     "⇄",
  "renewal":            "↻",
  "lease renewal":      "↻",
  "consolidation":      "⤢",
  "market consolidation":"⤢",
  "opening":            "＋",
  "new office":         "＋",
};

function runIcon(name) {
  const n = (name || "").toLowerCase();
  for (const [key, icon] of Object.entries(ICON_MAP)) {
    if (n.includes(key)) return icon;
  }
  return "▶";
}

function fmtDate(d) {
  if (!d) return "—";
  const raw = d instanceof Date ? d.toISOString() : String(d);
  const iso  = raw.slice(0, 10);
  const dt   = new Date(iso + "T12:00:00Z");
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// ─── StatusPill ───────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const map = {
    active:      { bg: T.blueSoft,  color: T.blue,  label: "Active"    },
    in_progress: { bg: T.blueSoft,  color: T.blue,  label: "Active"    },
    completed:   { bg: T.greenSoft, color: T.green, label: "Completed" },
    cancelled:   { bg: T.lightGray, color: T.mid,   label: "Cancelled" },
  };
  const s = map[status] ?? map.active;
  return (
    <span style={{
      fontSize: T.fsXxs5, fontWeight: T.fwBold, textTransform: "uppercase",
      letterSpacing: "0.03em", padding: "2px 8px", borderRadius: 6,
      background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

// ─── ProgressCell ─────────────────────────────────────────────────────────────

function ProgressCell({ run }) {
  const isCompleted  = run.status === "completed";
  const isCancelled  = run.status === "cancelled";
  const phaseCount   = run.phaseCount  ?? 1;
  const phasesDone   = run.phasesDone  ?? 0;
  const pct          = isCompleted ? 100 : Math.round((phasesDone / Math.max(phaseCount, 1)) * 100);

  if (isCancelled) {
    return <span style={{ fontSize: T.fsXs, color: T.light }}>— switched to exit</span>;
  }

  return (
    <span style={{ fontSize: T.fsSm, display: "inline-flex", alignItems: "center", gap: 0 }}>
      <span style={{
        display: "inline-block", width: 64, height: 6, borderRadius: 4,
        background: T.lightGray, verticalAlign: "middle", marginRight: 7,
        position: "relative",
      }}>
        <span style={{
          display: "block", height: "100%", borderRadius: 4,
          background: isCompleted ? T.green : T.blue,
          width: `${pct}%`,
        }} />
      </span>
      <span style={{ color: T.mid, fontSize: T.fsXs }}>
        {isCompleted ? "Done" : `Phase ${Math.min(phasesDone + 1, phaseCount)}/${phaseCount}`}
      </span>
    </span>
  );
}

// ─── Status tab helpers ───────────────────────────────────────────────────────

const TAB_DEFS = [
  { key: "all",       label: "All"       },
  { key: "active",    label: "Active"    },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

function matchesTab(run, tabKey) {
  if (tabKey === "all") return true;
  if (tabKey === "active")    return run.status === "active" || run.status === "in_progress";
  if (tabKey === "completed") return run.status === "completed";
  if (tabKey === "cancelled") return run.status === "cancelled";
  return true;
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: "last_activity", label: "Last activity" },
  { value: "started",       label: "Started"       },
  { value: "completed",     label: "Completed"     },
  { value: "progress",      label: "Progress"      },
];

function applySortRuns(runs, sortKey) {
  return [...runs].sort((a, b) => {
    if (sortKey === "started") {
      return new Date(b.startedAt ?? 0) - new Date(a.startedAt ?? 0);
    }
    if (sortKey === "completed") {
      return new Date(b.completedAt ?? 0) - new Date(a.completedAt ?? 0);
    }
    if (sortKey === "progress") {
      const pA = (a.phasesDone ?? 0) / Math.max(a.phaseCount ?? 1, 1);
      const pB = (b.phasesDone ?? 0) / Math.max(b.phaseCount ?? 1, 1);
      return pB - pA;
    }
    // default: last_activity
    return new Date(b.lastActivityAt ?? b.updatedAt ?? 0) - new Date(a.lastActivityAt ?? a.updatedAt ?? 0);
  });
}

// ─── PlaybookRunsPage ─────────────────────────────────────────────────────────

function PlaybookRunsListOwner() {
  const navigate = useNavigate();

  const [runs,    setRuns]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [tab,     setTab]     = useState("all");
  const [search,  setSearch]  = useState("");
  const [sort,    setSort]    = useState("last_activity");

  useEffect(() => {
    let cancelled = false;
    getPlaybookRuns()
      .then((list) => {
        if (!cancelled) { setRuns(Array.isArray(list) ? list : []); setLoading(false); }
      })
      .catch((err) => {
        log.error("PlaybookRunsPage: failed to load runs", err);
        if (!cancelled) { setError("Could not load runs. Please try again."); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, []);

  // Counts for tab badges
  const counts = useMemo(() => ({
    all:       runs.length,
    active:    runs.filter((r) => r.status === "active" || r.status === "in_progress").length,
    completed: runs.filter((r) => r.status === "completed").length,
    cancelled: runs.filter((r) => r.status === "cancelled").length,
  }), [runs]);

  // Filter + sort
  const displayed = useMemo(() => {
    let list = runs.filter((r) => matchesTab(r, tab));
    if (search.trim()) {
      const q = search.trim();
      list = list.filter((r) =>
        matchesFoldedQuery(q, r.name, r.targetLabel, r.owner)
      );
    }
    return applySortRuns(list, sort);
  }, [runs, tab, search, sort]);

  const isReadOnly = (run) => run.status === "completed" || run.status === "cancelled";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      data-testid="playbook-runs-page"
      style={{ maxWidth: 1080, margin: "0 auto", padding: "20px 24px 70px" }}
    >
      {/* Breadcrumb */}
      <div style={{ fontSize: T.fsXs5, color: T.light, fontWeight: T.fwBold, marginBottom: 6 }}>
        <button
          type="button"
          data-testid="back-to-playbooks"
          onClick={() => navigate("/playbooks")}
          style={{
            background: "transparent", border: "none", padding: 0,
            color: T.mid, cursor: "pointer", fontSize: T.fsXs5, fontWeight: T.fwBold,
            display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          <ArrowLeft size={12} />
          <span style={{ color: T.mid }}>Playbooks</span>
        </button>
        {" "}
        <span style={{ color: T.light }}>› Runs</span>
      </div>

      {/* Heading */}
      <h1 style={{ fontSize: T.fsLgX, fontWeight: T.fwBold, color: T.dark, margin: "0 0 2px" }}>
        Playbook runs
      </h1>
      <div style={{ fontSize: T.fsSm5, color: T.mid, marginBottom: 14 }}>
        Every run — in flight and completed. Completed runs are read-only records.
      </div>

      {/* Status tabs */}
      <div style={{
        display: "inline-flex",
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: 10, overflow: "hidden", marginBottom: 14,
      }}>
        {TAB_DEFS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            data-testid={`tab-${key}`}
            onClick={() => setTab(key)}
            style={{
              border: "none",
              background: tab === key ? T.dark : "transparent",
              color: tab === key ? T.white : T.mid,
              fontWeight: T.fwBold, fontSize: T.fsSm5,
              padding: "8px 15px", cursor: "pointer",
            }}
          >
            {label}
            {counts[key] > 0 && (
              <span style={{
                fontWeight: T.fwBold, opacity: 0.7, marginLeft: 5, fontSize: T.fsXs,
              }}>
                {counts[key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        {/* Search */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          flex: "0 0 280px",
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: 9, padding: "8px 12px",
        }}>
          <Search size={13} color={T.light} />
          <input
            data-testid="runs-search"
            type="text"
            placeholder="Search by office, lease, market, or owner…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              border: "none", outline: "none", flex: 1,
              fontSize: T.fsSm5, color: T.dark, background: "transparent",
            }}
          />
        </div>

        {/* Filter button (placeholder) */}
        <button
          type="button"
          data-testid="runs-filter-btn"
          style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: 9, padding: "8px 12px",
            fontWeight: T.fwBold, fontSize: T.fsSm5,
            color: T.dark, cursor: "pointer",
          }}
        >
          Filter: Type · Owner · Date
        </button>

        <div style={{ flex: 1 }} />

        {/* Sort */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: T.fsXs, color: T.mid }}>Sort:</span>
          <select
            data-testid="runs-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            style={{
              background: T.white, border: `1px solid ${T.border}`,
              borderRadius: 9, padding: "8px 10px",
              fontWeight: T.fwBold, fontSize: T.fsSm5,
              color: T.dark, cursor: "pointer",
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: T.mid, padding: "24px 0" }}>
          <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
          <span style={{ fontSize: T.fsSm5 }}>Loading runs…</span>
        </div>
      ) : error ? (
        <div style={{ fontSize: T.fsSm5, color: T.red, padding: "24px 0" }}>{error}</div>
      ) : (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: 12, overflowX: "auto",
        }}>
          <table
            data-testid="runs-table"
            style={{ width: "100%", borderCollapse: "collapse" }}
          >
            <thead>
              <tr>
                {["Playbook", "Target", "Status", "Progress", "Owner", "Started", "Last activity / Outcome", ""].map((h) => (
                  <th
                    key={h}
                    style={{
                      fontSize: T.fsXxs5, letterSpacing: "0.05em", textTransform: "uppercase",
                      color: T.mid, fontWeight: T.fwBold, textAlign: "left",
                      padding: "10px 14px",
                      borderBottom: `1px solid ${T.border}`,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      padding: "24px 14px", textAlign: "center",
                      fontSize: T.fsSm5, color: T.mid,
                    }}
                  >
                    {search ? "No runs match your search." : "No runs yet."}
                  </td>
                </tr>
              ) : (
                displayed.map((run, rowIdx) => {
                  const readOnly = isReadOnly(run);
                  const icon     = runIcon(run.name);
                  const isLast   = rowIdx === displayed.length - 1;

                  return (
                    <tr
                      key={run.id}
                      data-testid={`run-row-${run.id}`}
                      onClick={() => navigate(`/playbooks/runs/${encodeURIComponent(run.id)}`)}
                      style={{
                        cursor: "pointer",
                        borderBottom: isLast ? "none" : `1px solid ${T.lightGray}`,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = T.hoverBg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                    >
                      {/* Playbook name */}
                      <td style={{ padding: "11px 14px", verticalAlign: "middle" }}>
                        <span style={{ fontWeight: T.fwBold, fontSize: T.fsSm5 }}>
                          <span style={{
                            display: "inline-grid", placeItems: "center",
                            width: 22, height: 22, borderRadius: 6,
                            background: T.blueSoft, color: T.dark,
                            fontSize: T.fsSm, marginRight: 8, verticalAlign: "middle",
                          }}>
                            {icon}
                          </span>
                          {run.name}
                        </span>
                      </td>

                      {/* Target */}
                      <td style={{ padding: "11px 14px", verticalAlign: "middle", fontSize: T.fsSm5, color: T.mid }}>
                        {run.targetLabel ?? "—"}
                      </td>

                      {/* Status */}
                      <td style={{ padding: "11px 14px", verticalAlign: "middle" }}>
                        <StatusPill status={run.status} />
                      </td>

                      {/* Progress */}
                      <td style={{ padding: "11px 14px", verticalAlign: "middle" }}>
                        <ProgressCell run={run} />
                      </td>

                      {/* Owner */}
                      <td style={{ padding: "11px 14px", verticalAlign: "middle", fontSize: T.fsSm5, color: T.dark }}>
                        {run.owner ?? "—"}
                      </td>

                      {/* Started */}
                      <td style={{ padding: "11px 14px", verticalAlign: "middle", fontSize: T.fsSm5, color: T.dark }}>
                        {fmtDate(run.startedAt)}
                      </td>

                      {/* Last activity / Outcome */}
                      <td style={{ padding: "11px 14px", verticalAlign: "middle", fontSize: T.fsXs, color: T.light }}>
                        {run.completedAt
                          ? fmtDate(run.completedAt)
                          : run.lastActivityAt
                          ? fmtDate(run.lastActivityAt)
                          : "—"
                        }
                      </td>

                      {/* Open / View link */}
                      <td
                        style={{ padding: "11px 14px", verticalAlign: "middle" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/playbooks/runs/${encodeURIComponent(run.id)}`);
                        }}
                      >
                        <span style={{
                          color: T.blue, fontWeight: T.fwBold, fontSize: T.fsSm5,
                          cursor: "pointer",
                        }}>
                          {readOnly ? "View →" : "Open →"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
